import React, { useEffect, useLayoutEffect, useState } from "react";
import {
  Nav,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Modal,
  ModalHeader,
  ModalBody,
  Button,
  FormGroup,
  Input,
  Label,
  FormFeedback,
  Form,
} from "reactstrap";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEdit,
  faEye,
  faEyeSlash,
  faUserGroup,
  faHistory,
  faWallet,
  faGaugeHigh,
  faArrowRightFromBracket,
  faUser,
} from "@fortawesome/free-solid-svg-icons";
import { Check, ChevronDown, LogOut, Monitor, Moon, Sun, User2Icon } from "lucide-react";
import Notifications from "../../components/notifications/Notifications";
import getDataFromApi from "../../components/getDataFromApi";
import axios from "axios";
import { ContextManager } from "../../logic/context/ContextManager";
import { StyledTooltip } from "../project/label/annotation/EditorView/EditorTopNavigationBar/EditorTopNavigationBar";
import { getCokkie, logout } from "../login/Login";
import { AdminConsoleIcon, CloseIcon, DeviceIcon, DocsFileIcon, GearIcon, HubNodesIcon, LightbulbIcon, MenuCardIcon } from "../Common/SvgIcons/WorkflowIcons";

export var navActive = () => { };
export function onSubmit(formData, result) {
  localStorage.setItem(
    "hotKeys",
    JSON.stringify(result?.detail?.hot_keys ?? [])
  );
  ContextManager.updateHotkeys();
}

// ----- constants -----
const BASE_DEEP_VIEWS = ["deepdata", "deeplabel", "deeptrain", "deepdeploy"];
const ITEMS = [
  { title: "projects", path: "/projects", Icon: MenuCardIcon },
  { title: "devices", path: "/devices", Icon: DeviceIcon },
  { title: "hub", path: "/hub", Icon: HubNodesIcon },
  { title: "solutions", path: "/solutions", Icon: LightbulbIcon },
  { title: "docs", path: "/docs", Icon: DocsFileIcon },
];

const BottomItems = [
  { title: "projects", path: "/projects", Icon: MenuCardIcon },
  { title: "devices", path: "/devices", Icon: DeviceIcon },
  { title: "hub", path: "/hub", Icon: HubNodesIcon },
  { title: "solutions", path: "/solutions", Icon: LightbulbIcon },
  { title: "docs", path: "/docs", Icon: DocsFileIcon },
];

const SOLUTIONS_ONLY_ROLE = "solution_user";
const AMBARELLA_HUB_PATH_KEY = "ambarellaHubPath";

function isAmbarellaCustomer() {
  return window.ENV?.CUSTOMER === "ambarella";
}

function isAmbarellaEvaluationPath(pathname = "") {
  return (
    pathname === "/hub/model_page" ||
    /^\/hub\/[^/]+\/evaluation(?:\/|$)/.test(pathname)
  );
}

function rememberAmbarellaHubPath(pathname, search = "") {
  if (!isAmbarellaCustomer() || !isAmbarellaEvaluationPath(pathname)) return;
  sessionStorage.setItem(AMBARELLA_HUB_PATH_KEY, `${pathname}${search || ""}`);
}

const SOLUTION_ROLE_NAV_TITLES = {
  owner: ["devices", "hub", "solutions", "docs"],
  executive: ["solutions", "docs"],
  manager: ["solutions", "docs"],
  supervisor: ["solutions", "docs"],
  inspector: ["solutions", "docs"],
  technician: ["solutions", "docs"],
};

const getNavItemsByRole = (role, solutionRole) => {
  if (role === SOLUTIONS_ONLY_ROLE) {
    const allowed = SOLUTION_ROLE_NAV_TITLES[solutionRole] || SOLUTION_ROLE_NAV_TITLES.owner;
    return allowed
      .map((title) => {
        const item = ITEMS.find((i) => i.title === title);
        if (!item) return null;
        // Executives only use the VI dashboard — keep Solutions nav highlight, different path
        if (solutionRole === "executive" && title === "solutions") {
          return { ...item, path: "/vi-dashboard" };
        }
        return item;
      })
      .filter(Boolean);
  }
  return ITEMS;
};

const getBottomNavItemsByRole = (role, solutionRole) => {
  if (role === SOLUTIONS_ONLY_ROLE) {
    const allowed = SOLUTION_ROLE_NAV_TITLES[solutionRole] || SOLUTION_ROLE_NAV_TITLES.owner;
    return allowed
      .map((title) => {
        const item = BottomItems.find((i) => i.title === title);
        if (!item) return null;
        if (solutionRole === "executive" && title === "solutions") {
          return { ...item, path: "/vi-dashboard" };
        }
        return item;
      })
      .filter(Boolean);
  }
  return BottomItems;
};


const ADMIN_ITEMS = [
  { title: "admin panel", label: "Dashboard", path: "/admin", icon: faGaugeHigh, text: "Overview of platform activity", text2: "Get an overview of platform activity, key metrics, and real-time insights. Stay on top of operations with a centralized control panel that puts critical data at your fingertips." },
  { title: "logs", label: "Log Viewer", path: "/admin/logs", icon: faHistory, text: "Trace activities & errors", text2: "Monitor system logs to track activities, errors, and performance for better troubleshooting." },
  { title: "users", label: "User Management", path: "/admin/users", icon: faUserGroup, text: "Manage roles & access", text2: "Add, edit, and manage users seamlessly while keeping track of roles and access." },
  { title: "subscription", label: "Subscription Plans", path: "/admin/subscription", icon: faWallet, text: "Billing & renewals", text2: "Manage subscription plans, billing, and renewals with ease to keep users engaged." },
];



function useTheme() {
  const html = document.documentElement;

  const [preference, setPreference] = useState(
    () => localStorage.getItem("theme") || "system"
  );

  useLayoutEffect(() => {
    const isDarkSystem =
      window.matchMedia("(prefers-color-scheme: dark)").matches;
    const actual =
      preference === "light"
        ? "light"
        : preference === "dark"
          ? "dark"
          : isDarkSystem
            ? "dark"
            : "light";

    html.setAttribute("data-theme", actual);
    localStorage.setItem("theme", preference);
  }, [preference]);


  useEffect(() => {
    const mql = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = (e) => {
      if (preference === "system") {
        html.setAttribute("data-theme", e.matches ? "dark" : "light");
      }
    };
    mql.addEventListener("change", onChange);
    return () => mql.removeEventListener("change", onChange);
  }, [preference]);

  return [
    preference,
    () => setPreference("light"),
    () => setPreference("dark"),
    () => setPreference("system"),
  ];
}



// ----- main Sidenav component -----
export default function Sidenav({ dashboardData, getDisabled, viewSet }) {
  const { pathname, search } = useLocation();
  const parts = pathname.split("/");
  const view = parts[1];
  const subView = parts[3];

  const vieww = window.location.pathname.split("/")[3];
  const projectId = window.location.pathname.split("/")[2];
  const projectName = window.location.pathname.split("/")[1];

  // modal states
  const [showProfile, setShowProfile] = useState(false);
  const [path, setPath] = useState("");
  const [open, setOpen] = React.useState(false);

  useEffect(() => {
    rememberAmbarellaHubPath(pathname, search);
  }, [pathname, search]);

  useEffect(() => {
    const root = document.documentElement;
    if (isAmbarellaCustomer()) {
      root.setAttribute("data-customer", "ambarella");
      return () => root.removeAttribute("data-customer");
    }
    return undefined;
  }, []);

  // active-tab logic
  const [active, setActive] = useState("dashboard");
  useEffect(() => {
    // Plant/VI analytics routes live under /vi-dashboard but belong to Solutions.
    const solutionsScoped =
      /^\/vi-[Dd]ashboard(?:\/|$)/.test(pathname) ||
      /^\/plants(?:\/|$)/.test(pathname) ||
      /^\/stationstab(?:\/|$)/.test(pathname);
    const matchPath = solutionsScoped ? "/solutions" : pathname;

    const found =
      ITEMS.find((i) =>
        i.title === "docs"
          ? matchPath.startsWith("/docs")
          : matchPath.startsWith(i.path)
      ) ||
      ADMIN_ITEMS.find((i) => pathname.startsWith(i.path));
    setActive(found?.title || "dashboard");
  }, [pathname]);

  // load hotkeys into ContextManager once
  useEffect(() => {
    ContextManager.updateHotkeys();
  }, []);

  // theme hook
  const [theme, toLight, toDark, toSystem] = useTheme();

  // expose navActive
  navActive = () => setActive("projects");

  const isSideNav =
    BASE_DEEP_VIEWS.includes(subView) || view === "annotations";

  return isSideNav ? (
    <>
      <TopNav
        widthset={true}
        active={active}
        onActivate={setActive}
        showProfile={showProfile}
        setShowProfile={setShowProfile}
        path={path}
        setPath={setPath}
        theme={theme}
        toLight={toLight}
        toDark={toDark}
        toSystem={toSystem}
        view={'projects'}
      />

    </>) : (
    <TopNav
      widthset={false}
      active={active}
      onActivate={setActive}
      showProfile={showProfile}
      setShowProfile={setShowProfile}
      path={path}
      setPath={setPath}
      theme={theme}
      toLight={toLight}
      toDark={toDark}
      toSystem={toSystem}
      view={view}
    />
  );
}


function TopNav({
  active,
  onActivate,
  theme,
  toLight,
  toDark,
  toSystem,
  view
}) {

  // Keep top + bottom navbars always visible while scrolling (no hide / compact)
  const role = localStorage.getItem("role");
  const solutionRole = localStorage.getItem("solutionRole");
  const navItems = getNavItemsByRole(role, solutionRole);
  const bottomNavItems = getBottomNavItemsByRole(role, solutionRole);
  const [adminOpen, setAdminOpen] = useState(false);

  // --- Inline profile state + helpers (dropdown profile card) ---
  const [navUser, setNavUser] = useState(null);          // { name, email, role, profile_image, ... }
  const [navLoading, setNavLoading] = useState(false);

  // separate per-field edit for Role
  const [editField, setEditField] = useState(null);      // "role" | null
  const [navSaving, setNavSaving] = useState({});        // { role: boolean }
  const [draft, setDraft] = useState({});                // drafts per field

  // identity (avatar + name + email) edited together
  const [editIdentity, setEditIdentity] = useState(false);
  const [savingIdentity, setSavingIdentity] = useState(false);
  const [themeOpen, setThemeOpen] = useState(false);


  const navigate = useNavigate()
  const isAmbarella = isAmbarellaCustomer();
  const displayNavItems = navItems;
  const displayBottomNavItems = bottomNavItems;
  const navAvatar = navUser?.profile_image || localStorage.getItem("navImage") || "/ico/user.png";
  const navName = navUser?.name || localStorage.getItem("name") || "User";
  const navEmail = navUser?.email || localStorage.getItem("email") || "";
  const navRole = navUser?.role || localStorage.getItem("role") || "";
  const hasCustomAvatar = Boolean(navUser?.profile_image || localStorage.getItem("navImage"));
  const navInitials = (() => {
    const parts = String(navName || "").trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return "U";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
  })();
  const themeLabel = theme === "dark" ? "Dark" : theme === "system" ? "System" : "Light";
  const ThemeIcon = theme === "dark" ? Moon : theme === "system" ? Monitor : Sun;

  // Prefetch profile in the background when navbar mounts so the
  // dropdown opens with details ready (no "Loading profile..." wait).
  useEffect(() => {
    let cancelled = false;

    const prefetch = async () => {
      try {
        setNavLoading(true);
        const res = await getDataFromApi(`${window.ENV.API_URL}/home/users`, "get");
        if (cancelled) return;
        setNavUser(res);
        setDraft(res || {});
        if (res?.profile_image) localStorage.setItem("navImage", res.profile_image);
        if (res?.name) localStorage.setItem("name", res.name);
        if (res?.email) localStorage.setItem("email", res.email);
        if (res?.role) localStorage.setItem("role", res.role);
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setNavLoading(false);
      }
    };

    prefetch();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- helpers ---
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if ((reader.result || "").includes("data:image")) resolve(reader.result);
        else reject("Not an image");
      };
      reader.onerror = (err) => reject(err);
    });

  const startEdit = (field) => {
    setEditField(field);
    setDraft((d) => ({ ...d, [field]: navUser?.[field] ?? "" }));
  };
  const cancelEdit = () => {
    if (!editField) return;
    const f = editField;
    setEditField(null);
    setDraft((d) => ({ ...d, [f]: navUser?.[f] ?? "" }));
  };
  const saveField = async (field) => {
    const value = (draft[field] ?? "").toString().trim();
    if (!value) return;
    try {
      setNavSaving((s) => ({ ...s, [field]: true }));
      const cokkie = getCokkie("jwtToken");
      const url = `${window["ENV"].API_URL}/home/users`;
      const headers = { accept: "application/json", Authorization: `Bearer ${cokkie}` };
      await axios.put(url, { [field]: value }, { headers });  // update only that field
      setNavUser((u) => ({ ...u, [field]: value }));
      setEditField(null);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data || `Failed updating ${field}`);
    } finally {
      setNavSaving((s) => ({ ...s, [field]: false }));
    }
  };

  // ----- Identity block (avatar + name + email) edited together -----
  const IdentityBlock = () => {
    const [local, setLocal] = useState({
      profile_image: navUser?.profile_image || localStorage.getItem("navImage") || "",
      name: navUser?.name || localStorage.getItem("name") || "",
      email: navUser?.email || "",
      role: localStorage.getItem("role") || "",
    });

    useEffect(() => {
      setLocal({
        profile_image: navUser?.profile_image || localStorage.getItem("navImage") || "",
        name: navUser?.name || localStorage.getItem("name") || "",
        email: navUser?.email || "",
        role: navUser?.role || "",
      });
    }, [navUser]);

    const saveIdentity = async () => {
      try {
        setSavingIdentity(true);
        const cokkie = getCokkie("jwtToken");
        const url = `${window.ENV.API_URL}/home/users`;
        const headers = { accept: "application/json", Authorization: `Bearer ${cokkie}` };

        await axios.put(
          url,
          {
            name: local.name,
            email: local.email,
            role: local.role,
            profile_image: local.profile_image,
          },
          { headers }
        );

        setNavUser((u) => ({
          ...(u || {}),
          name: local.name,
          email: local.email,
          role: local.role,
          profile_image: local.profile_image,
        }));
        localStorage.setItem("name", local.name);
        if (local.profile_image) localStorage.setItem("navImage", local.profile_image);

        setEditIdentity(false);
      } catch (e) {
        console.error(e);
        alert(e?.response?.data || "Failed to update profile.");
      } finally {
        setSavingIdentity(false);
      }
    };

    if (!editIdentity) {
      // VIEW MODE — one pencil edits the whole identity
      return (
        <>
          <div className="d-flex align-items-start justify-content-between">
            <div className="d-a gap-2 flex-row">
              <img
                className="small-img rounded-img"
                src={local.profile_image}
                style={{ width: 40, height: 40 }}
                alt=""
              />
              <div>
                <div className="fw-semibold">{local.name || "-"}</div>
                <div className="text-secondary small">{local.email || " "}</div>
              </div>
            </div>
          </div>
          <div className="mt-3 text-secondary" style={{ textTransform: 'capitalize' }}> <b>{localStorage.getItem("role") === "solution_user" && localStorage.getItem("solutionRole") ? localStorage.getItem("solutionRole")?.replace(/_/g, " ") : localStorage.getItem("role")?.replace(/_/g, " ")}</b></div >
        </>
      );
    }

    // EDIT MODE — underline inputs + single ✓/✕
    return (
      <>
        <div className="d-a justify-content-between gap-2 flex-row">
          <div className="d-flex align-items-center">
            <label htmlFor="nav-avatar" className="m-0" style={{ cursor: "pointer" }}>
              <img
                className="rounded-img"
                src={local.profile_image}
                style={{ width: 40, height: 40, objectFit: "cover" }}
                alt=""
                title="Change photo"
              />
            </label>
            <input
              id="nav-avatar"
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={async (e) => {
                if (e.target?.files?.length) {
                  try {
                    const b64 = await toBase64(e.target.files[0]);
                    setLocal((s) => ({ ...s, profile_image: b64 }));
                  } catch {
                    alert("Select a valid image.");
                  }
                }
              }}
            />
          </div>

          <div className="w-100">
            <Input
              className="side-nav-profile-edit-input p-0"
              bsSize="sm"
              value={local.name}
              onChange={(e) => setLocal((s) => ({ ...s, name: e.target.value }))}
            />

            <Input
              className="side-nav-profile-edit-input p-0 mt-1"
              bsSize="sm"
              type="email"
              value={local.email}
              onChange={(e) => setLocal((s) => ({ ...s, email: e.target.value }))}
            />
          </div>

          <div className="d-flex justify-content-end gap-2">
            <Button className="p-1"
              size="sm"
              color="secondary"
              onClick={() => {
                setEditIdentity(false);
                setLocal({
                  profile_image: navUser?.profile_image || localStorage.getItem("navImage") || "",
                  name: navUser?.name || localStorage.getItem("name") || "",
                  email: navUser?.email || "",
                  role: navUser?.role || "",
                });
              }}
              disabled={savingIdentity}
            >
              ✕
            </Button>
            <Button className="p-1" size="sm" onClick={saveIdentity} disabled={savingIdentity}>
              {savingIdentity ? "…" : "✓"}
            </Button>
          </div>
        </div>
        <div className="mt-3 text-secondary" style={{ textTransform: 'capitalize' }}> <b>{localStorage.getItem("role") === "solution_user" && localStorage.getItem("solutionRole") ? localStorage.getItem("solutionRole")?.replace(/_/g, " ") : localStorage.getItem("role")?.replace(/_/g, " ")}</b></div >
      </>
    );
  };

  return (
    <>
      <div className="w-100 top-navbar-main">
        <div className="d-a justify-content-between navbar pt-2">
          {/* <Link to="/projects" className={`navbar-brand ${view === 'projects' && 'ms-1'}`}> */}
          <img src={window.ENV.LOGO_URL} alt="logo" style={{ maxWidth: '120px', maxHeight: '50px' }} />
          {/* </Link> */}
          {!isAmbarella && (
          <Nav className="d-flex align-items-center nav-links flex-fill de-nav-links">
              {displayNavItems.map((item) => (
                <Link
                  key={item.title}
                  to={item.path}
                  className={`sidenav-links mb-0 ${active === item.title ? "active sidenav-links-active" : ""}`}
                  onClick={() => onActivate(item.title)}
                  style={{
                    color: active === item.title ? 'var(--leading-color)' : '',
                    borderBottom: active === item.title ? '2px solid var(--leading-color)' : '2px solid transparent',
                  }}
                >
                  {item.title.charAt(0).toUpperCase() + item.title.slice(1)}
                </Link>
              ))}

            </Nav>
          )}

          <ul className="d-a mb-0 ps-0 gap-1">
            <Notifications />
            <br />
            <UncontrolledDropdown className="p-0 mb-1 border-0">
              <DropdownToggle className="p-0 border-0">
                <StyledTooltip title="Profile">
                  <h6 className="d-j-a gap-2 m-0">
                    {hasCustomAvatar ? (
                      <img
                        className="small-img rounded-img shadow"
                        src={navAvatar}
                        style={{ width: '30px', height: '30px' }}
                        alt=""
                      />
                    ) : (
                      <div
                        className="nav-profile-avatar nav-profile-avatar-initials small-img rounded-img shadow"
                        style={{ width: '30px', height: '30px', fontSize: '11px' }}
                        aria-hidden="true"
                      >
                        {navInitials}
                      </div>
                    )}
                  </h6>
                </StyledTooltip>
              </DropdownToggle>

              <DropdownMenu right className="nav-profile-menu mt-3">
                <div className="nav-profile-header">
                  <div className="d-a justify-content-between align-items-start gap-2">
                    <div className="flex-fill min-w-0">
                      <div className="nav-profile-header-button">
                        {hasCustomAvatar ? (
                          <img
                            className="nav-profile-avatar rounded-img shadow"
                            src={navAvatar}
                            alt={navName}
                          />
                        ) : (
                          <div className="nav-profile-avatar nav-profile-avatar-initials" aria-hidden="true">
                            {navInitials}
                          </div>
                        )}
                        <div className="nav-profile-copy w-100">
                          <div className="nav-profile-name">{navName}</div>
                          <div className="nav-profile-email m-0">
                            {navLoading && !navEmail ? "Loading profile..." : (navEmail || "No email added")}
                          </div>
                        </div>
                      </div>
                      {navRole ? (
                        <span className="nav-profile-role-badge text-capitalize">
                          {navRole === "solution_user" && solutionRole ? String(solutionRole).replace(/_/g, " ") : String(navRole).replace(/_/g, " ")}
                        </span>
                      ) : null}
                    </div>
                    <DropdownItem
                      className="nav-profile-close p-0 border-0 bg-transparent text-end w-auto"
                      aria-label="Close profile menu"
                    >
                      <CloseIcon className="wf-icon text-secondary-small text-secondary" />
                    </DropdownItem>
                  </div>
                </div>

                <div className="nav-profile-section">
                  {!isAmbarella && (
                  <DropdownItem
                    className="nav-profile-item"
                    onClick={() =>
                      navigate("/user-profile", {
                        state: {
                          user: navUser,
                          id: navUser?.id || localStorage.getItem("user_id"),
                          viewFrom: "profile_dashboard",
                        },
                      })
                    }
                  >
                    <User2Icon className="wf-icon-small" />
                    <span>My Account</span>
                  </DropdownItem>
                  )}

                  {!isAmbarella && (
                  <DropdownItem
                    className="nav-profile-item"
                    onClick={() => {
                      const userId = navUser?.id || localStorage.getItem("user_id");
                      navigate("/admin/user-profile-edit", {
                        state: {
                          user: navUser,
                          id: userId,
                          viewFrom: "profile_dashboard",
                        },
                      });
                    }}
                  >
                    <GearIcon className="wf-icon-small" />
                    <span>Manage Account</span>
                  </DropdownItem>
                  )}

                  {(role === "system_admin" || role === "tenant_admin") && (
                    <DropdownItem
                      className="nav-profile-item"
                      onClick={() => { onActivate("admin"); navigate('/admin') }}
                    >
                      <AdminConsoleIcon className="wf-icon-small" />
                      <span>Admin Console</span>
                    </DropdownItem>
                  )}

                  <button
                    type="button"
                    className={`nav-profile-item nav-profile-theme-toggle ${themeOpen ? "open" : ""}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setThemeOpen((open) => !open);
                    }}
                  >
                    <ThemeIcon size={16} strokeWidth={2} className="nav-profile-lucide-icon" />
                    <span>Display Theme</span>
                    <span className="nav-profile-theme-value">
                      {themeLabel}
                      <ChevronDown size={14} strokeWidth={2} className="nav-profile-theme-chevron" />
                    </span>
                  </button>

                  {themeOpen && (
                    <div
                      className="nav-profile-theme-panel p-0"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                      }}
                    >
                      {[
                        { key: "light", label: "Light", Icon: Sun, apply: toLight },
                        { key: "dark", label: "Dark", Icon: Moon, apply: toDark },
                        { key: "system", label: "System", Icon: Monitor, apply: toSystem },
                      ].map((option) => (
                        <button
                          key={option.key}
                          type="button"
                          className={`nav-profile-theme-option ${theme === option.key ? "active" : ""}`}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            option.apply();
                          }}
                        >
                          <span className="nav-profile-theme-option-label">
                            {/* <option.Icon size={15} strokeWidth={2} /> */}
                            {option.label}
                          </span>
                          {theme === option.key ? <Check size={15} strokeWidth={2.5} /> : null}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <div className="nav-profile-section nav-profile-section-signout">
                  <DropdownItem className="nav-profile-signout" onClick={logout}>
                  <LogOut className="wf-icon-small" />
                    <span>Sign out</span>
                  </DropdownItem>
                </div>
              </DropdownMenu>
            </UncontrolledDropdown>
          </ul>
        </div>
      </div>

      {!isAmbarella && (
      <div className="bottom-navbar glass-container">
        {displayBottomNavItems.map((item) => {
          const ItemIcon = item.Icon;
          const isActive = active === item.title;
          const content = (
            <>
              <span
                className={`bottom-navbar__icon${isActive ? " is-active" : ""}`}
              >
                {ItemIcon ? (
                  <ItemIcon className="bottom-navbar__svg" aria-hidden="true" />
                ) : null}
              </span>
              <span className="bottom-navbar__label">
                {item.title.charAt(0).toUpperCase() + item.title.slice(1)}
              </span>
            </>
          );

          return (
            <Link
              key={item.title}
              to={item.path}
              className={`bottom-navbar__item sidenav-lin d-j-a gap-1 flex-column cursor-pointer ${
                isActive ? "active sidenav-links-activ" : ""
              }`}
              onClick={() => onActivate(item.title)}
            >
              {content}
            </Link>
          );
        })}

      </div>
      )}
    </>
  );
}


// ----- Profile modal component -----
export function ViewProfile({ open, setOpen }) {
  const [edit, setEdit] = useState(false);
  const [showPassword, setShowPassword] = useState({
    oldPassword: false,
    newPassword: false,
  });
  const [data, setData] = useState({});
  const [editPassword, setEditPassword] = React.useState(false);
  const [validation, setValidation] = useState({});

  useEffect(() => {
    if (!open) {
      setData({});
      return;
    }
    getDataFromApi(`${window.ENV.API_URL}/home/users`, "get")
      .then(setData)
      .catch(console.error);
  }, [open]);
  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);

      fileReader.onload = () => {
        if (fileReader.result.includes("data:image"))
          resolve(fileReader.result);
        else {
          alert("select a valid image");
          reject("404");
        }
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };
  return (
    <Modal
      centered
      scrollable
      size=""
      isOpen={open}
      className="user-profile-modal"
      zIndex={30000}
    >
      <ModalHeader
        toggle={() => {
          setEdit(false);
          setOpen(false);
        }}
      >
        Profile
        <Button
          color="transparent"
          className="edit-profile"
          onClick={(e) => setEdit(!edit)}
        >
          <FontAwesomeIcon
            icon={faEdit}
            color={edit ? "#5c5cb8" : "#000"}
          ></FontAwesomeIcon>
        </Button>
      </ModalHeader>
      <ModalBody>
        <Form>
          <div className="img-wrapper d-flex justify-content-center">
            <img
              className="mb-3 profile-img"
              src={data.profile_image}
              alt="AVATAR.jpg"
            />
            {edit && (
              <>
                <Label for="file-upload" className="file-upload">
                  <FontAwesomeIcon
                    icon={faEdit}
                    color={"#ced4da"}
                    size="3x"
                  ></FontAwesomeIcon>
                </Label>
                <Input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  id="file-upload"
                  onChange={async (e) => {
                    if (e.target?.files?.length) {
                      const base64 = await convertBase64(e.target.files[0]);
                      setData({ ...data, profile_image: base64 });
                    }
                  }}
                />
              </>
            )}
          </div>
          <div className="d-flex justify-content-center fs-5">
            <b>Role:</b>
            <span className="mx-2 fst-italic">{data.role}</span>
          </div>
          <FormGroup>
            <Label for="name">Name:</Label>
            <Input
              type="text"
              id="name"
              placeholder="Name"
              readOnly={!edit}
              value={data.name}
              valid={validation?.name}
              onChange={(e) => setData({ ...data, name: e.target.value })}
            ></Input>
            <FormFeedback valid={validation?.name}>
              {validation.name}
            </FormFeedback>
          </FormGroup>
          <FormGroup>
            <Label for="email">Email:</Label>
            <Input
              type="Email"
              id="email"
              placeholder="example@example.com"
              value={data.email}
              readOnly={!edit}
              onChange={(e) => setData({ ...data, email: e.target.value })}
              invalid={validation.email ? true : false}
            ></Input>
          </FormGroup>
          <FormFeedback>{validation.email}</FormFeedback>
          {edit && (
            <>
              <div class="form-check form-switch d-flex align-items-center mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  role="switch"
                  checked={editPassword}
                  id="flexSwitchCheckDefault"
                  onChange={(e) => setEditPassword(e.target.checked)}
                />
                <Label
                  class="form-check-label mx-2"
                  for="flexSwitchCheckDefault"
                >
                  Update Password
                </Label>
              </div>
              {editPassword && (
                <>
                  <FormGroup>
                    <Label for="old-password">Type Old Password:</Label>
                    <div className="password-container">
                      <Input
                        type={showPassword.oldPassword ? "text" : "Password"}
                        autocomplete="new-password"
                        id="old-password"
                        placeholder="Old password"
                        readOnly={!edit}
                        onChange={(e) =>
                          setData({ ...data, oldPassword: e.target.value })
                        }
                        invalid={!validation.oldPassword ? false : true}
                      ></Input>
                      <FormFeedback>{validation.oldPassword}</FormFeedback>
                      <Button
                        onClick={(e) =>
                          setShowPassword({
                            ...showPassword,
                            oldPassword: !showPassword.oldPassword,
                          })
                        }
                        className="eye-btn"
                      >
                        <FontAwesomeIcon
                          icon={showPassword.oldPassword ? faEye : faEyeSlash}
                        />
                      </Button>
                    </div>
                  </FormGroup>
                  <FormGroup>
                    <Label for="new-password">Type New Password:</Label>
                    <div className="password-container">
                      <Input
                        type={showPassword.newPassword ? "text" : "Password"}
                        autocomplete="new-password"
                        id="new-password"
                        placeholder="New Password"
                        readOnly={!edit}
                        onChange={(e) =>
                          setData({ ...data, newPassword: e.target.value })
                        }
                        invalid={!validation.oldPassword ? false : true}
                      ></Input>
                      <Button
                        onClick={(e) =>
                          setShowPassword({
                            ...showPassword,
                            newPassword: !showPassword.newPassword,
                          })
                        }
                        className="eye-btn"
                      >
                        <FontAwesomeIcon
                          icon={showPassword.newPassword ? faEye : faEyeSlash}
                        />
                      </Button>
                    </div>
                  </FormGroup>
                </>
              )}
              <Button
                color="primary"
                className="block-center"
                onClick={async () => {
                  try {
                    const cokkie = getCokkie("jwtToken");
                    const url = `${window["ENV"].API_URL}/home/users`;
                    const headers = {
                      accept: "application/json",
                      Authorization: `Bearer ${cokkie}`,
                    };
                    const response = await axios.put(url, data, { headers });
                    console.log(response);
                    alert(response.data);
                    if (response.status == 200) {
                      localStorage.setItem("navImage", data.profile_image);
                      setEdit(false);
                      setOpen(false);
                    }
                  } catch (e) {
                    setValidation(e.response.data);
                    console.log(e.response.data);
                  }
                }}
              >
                Update Profile
              </Button>
            </>
          )}
        </Form>
      </ModalBody>
    </Modal>
  );
}
